import { Box, Tooltip } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { MarkdownConverter } from '@synergy/components-next';
import { MessageIcon } from '@synergy/theme';
import { PaloNoteTone, PaloNotice, PaloNotices, paloNoticeText } from '@synergy/components-palo';
import { PoolSubToolbarAlertModal } from '@synergy/components';
import { ReactElement, useState } from 'react';
import { ReceiptAlert, ReceiptAlertType } from '@synergy/models';
import { tokens } from '@synergy/theme-palo';
import { useParams } from 'react-router-dom';

import { InternalNote } from '../../models/internal-note';
import { InternalNoteFormValue, InternalNotesModal } from '../internal-notes/internal-notes-modal';
import { NOTE_WEIGHT, noteTone } from './palo-record-notices';
import { fetchReceiptInternalNotes, updateInternalNote } from '../../actions/internal-notes-actions';
import { receiptInternalNotesSelector, receiptNoteTypeSelector } from '../../selectors/internal-notes-selectors';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

const c = tokens.color;

const documentTone = (type?: ReceiptAlertType): PaloNoteTone => {
    switch (type) {
        case ReceiptAlertType.Danger:
            return 'danger';
        case ReceiptAlertType.Warning:
            return 'warn';
        default:
            return 'info';
    }
};

interface PaloDocumentNoticesProps {
    /** The document's own notice (Beleghinweis). */
    alert?: ReceiptAlert;
    /** Omit to render read-only, without the add and edit affordances. */
    onAlertChange?: (alert: ReceiptAlert | null) => void;
}

/**
 * Both kinds of notice, written out in the head of the Belegmaske.
 *
 * In the arrangement agreed on 2026-09-03 they stand together above the tabs,
 * so they are seen from every tab and without scrolling — and each one says
 * where it came from, because a notice on the supplier weighs differently from
 * one that concerns this document alone. Since 2026-09-22 each is shown in
 * full rather than as a one-line pill.
 *
 * Ordered by weight: a problem stands before a warning, a warning before a note.
 */
export const PaloDocumentNotices = ({ alert, onAlertChange }: PaloDocumentNoticesProps): ReactElement | null => {
    const notes = useAppSelector(receiptInternalNotesSelector);
    const noteType = useAppSelector(receiptNoteTypeSelector);
    const dispatch = useThunkDispatch();
    const { id: idParam } = useParams<{ id: string }>();

    const [noteToEdit, setNoteToEdit] = useState<InternalNote | undefined>(undefined);
    const [editingAlert, setEditingAlert] = useState(false);

    const masterNotes = [...(notes ?? [])].sort(
        (a, b) => NOTE_WEIGHT[noteTone(a.type)] - NOTE_WEIGHT[noteTone(b.type)]
    );

    const handleNoteSave = async (internalNote: InternalNoteFormValue): Promise<void> => {
        if (internalNote.id && noteType && idParam) {
            await dispatch(updateInternalNote({ ...internalNote, id: internalNote.id }));
            dispatch(fetchReceiptInternalNotes(noteType, +idParam));
        }
        setNoteToEdit(undefined);
    };

    // Nothing to show and nothing to add: the head stays as it was.
    if (0 === masterNotes.length && !alert && !onAlertChange) {
        return null;
    }

    return (
        <>
            <PaloNotices
                groups={[
                    masterNotes.map(note => {
                        const written = paloNoticeText(note.text);

                        return (
                            <PaloNotice
                                key={note.id}
                                tone={noteTone(note.type)}
                                title={written.title}
                                editLabel={<FormattedMessage id="app.common.edit" />}
                                onEdit={() => setNoteToEdit(note)}
                            >
                                <MarkdownConverter value={written.body} />
                            </PaloNotice>
                        );
                    }),
                    alert && (
                        <PaloNotice
                            tone={documentTone(alert.type)}
                            title={paloNoticeText(alert.text).title}
                            editLabel={<FormattedMessage id="app.common.edit" />}
                            onEdit={onAlertChange ? () => setEditingAlert(true) : undefined}
                        >
                            {/* Typed in a plain field, where a line break is
                                meant — and here it is the mark that stands for
                                one. */}
                            {paloNoticeText(alert.text).body}
                        </PaloNotice>
                    ),
                ]}
                action={
                    !alert &&
                    onAlertChange && (
                        <Tooltip title={<FormattedMessage id="components.receiptAlert.title.add" />}>
                            <Box
                                role="button"
                                onClick={() => setEditingAlert(true)}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    fontSize: '11.5px',
                                    color: c.ink3,
                                    cursor: 'pointer',
                                    '&:hover': { color: c.ink },
                                }}
                            >
                                <MessageIcon sx={{ fontSize: 15 }} />
                                <FormattedMessage id="components.receiptAlert.title.add" />
                            </Box>
                        </Tooltip>
                    )
                }
            />

            <InternalNotesModal
                internalNote={noteToEdit}
                open={undefined !== noteToEdit}
                onClose={() => setNoteToEdit(undefined)}
                onSave={handleNoteSave}
            />

            <PoolSubToolbarAlertModal
                isVisible={editingAlert}
                alert={alert}
                onClose={() => setEditingAlert(false)}
                onAlertChange={value => {
                    setEditingAlert(false);
                    onAlertChange?.(value);
                }}
                onAlertDelete={() => {
                    setEditingAlert(false);
                    onAlertChange?.(null);
                }}
            />
        </>
    );
};
